"use client";

import { useEffect, useRef, useState } from "react";
import { Zap, Hammer, Gem, Rocket, Compass } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function LeadershipMilestones() {
  const stages = [{
    number: "01",
    title: "THE DISCOVERY",
    description: "We begin with a deep dive into your organization's DNA. Identifying the friction points and untapped potential that define your current leadership landscape.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/leadership_discovery.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/leadership_discovery.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/leadership_discovery.png-wvc-sizes",
    alt: "Damien Cole in a strategy session",
    Icon: Compass
  }, {
    number: "02",
    title: "THE IGNITION",
    description: "The keynote experience. A high-energy catalyst designed to break through complacency and spark a collective commitment to the resilience mindset.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/keynote_ignition.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/keynote_ignition.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/keynote_ignition.png-wvc-sizes",
    alt: "Damien Cole speaking on stage",
    Icon: Zap
  }, {
    number: "03",
    title: "THE REFORGING",
    description: "Intensive workshops where theory meets practice. We hammer out new habits and empathy-driven frameworks through direct, high-impact coaching.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/leadership_coaching.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/leadership_coaching.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/leadership_coaching.png-wvc-sizes",
    alt: "Damien Cole coaching a team",
    Icon: Hammer
  }, {
    number: "04",
    title: "THE REFINEMENT",
    description: "Polishing the strategy. We implement feedback loops and performance metrics to ensure the new leadership culture is built to endure pressure.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/strategy_refinement.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/strategy_refinement.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/strategy_refinement.webp-wvc-sizes",
    alt: "Close up of leadership notes",
    Icon: Gem
  }, {
    number: "05",
    title: "THE MOMENTUM",
    description: "Sustained growth through ongoing support and podcast integration, ensuring the transformation integrates seamlessly into your daily operations.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/momentum_results.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/momentum_results.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/momentum_results.png-wvc-sizes",
    alt: "Audience reacting to a speech",
    Icon: Rocket
  }];
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [lineProgress, setLineProgress] = useState(0);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  useEffect(() => {
    const handleScroll = () => {
      const node = sectionRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const total = rect.height + viewportH * 0.5;
      const scrolled = Math.min(Math.max(viewportH - rect.top, 0), total);
      const progress = Math.min(scrolled / total, 1);
      setLineProgress(progress);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);
  return <section data-section-id="4231" id="leadership-milestones" ref={sectionRef} className="relative bg-background text-foreground overflow-hidden py-24 md:py-32 px-4 md:px-8">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
      background: "radial-gradient(ellipse at center, hsla(45, 100%, 50%, 0.08) 0%, hsla(215, 25%, 10%, 0) 60%)"
    }} />

      <div className="relative max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center mb-20 md:mb-28 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="font-mono text-xs md:text-sm tracking-widest text-primary mb-6 uppercase">THE METHODOLOGY</div>
          <h2 className="font-default font-black uppercase leading-[0.9] tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-[6rem]">
            <span className="block">FROM POTENTIAL</span>
            <span className="block">
              <span className="text-primary">/</span> TO PERFORMANCE
            </span>
          </h2>
        </div>

        {}
        <div className="relative">
          {}
          <div aria-hidden="true" className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-border" />
          {}
          <div aria-hidden="true" className="hidden md:block absolute left-1/2 top-0 -translate-x-1/2 w-px bg-primary transition-[height] duration-300 ease-out" style={{
          height: `${lineProgress * 100}%`
        }} />

          {}
          <div aria-hidden="true" className="md:hidden absolute left-6 top-0 bottom-0 w-px bg-border" />
          <div aria-hidden="true" className="md:hidden absolute left-6 top-0 w-px bg-primary transition-[height] duration-300 ease-out" style={{
          height: `${lineProgress * 100}%`
        }} />

          {}
          <div className="space-y-20 md:space-y-32">
            {stages.map((stage, i) => {
            const isLeft = i % 2 === 0;
            return <StageItem key={stage.number} data-index={i} stage={stage} isLeft={isLeft} index={i} />;
          })}
          </div>
        </div>
      </div>
    </section>;
}
type StageProps = {
  stage: {
    number: string;
    title: string;
    description: string;
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
    Icon: React.ComponentType<{
      className?: string;
    }>;
  };
  isLeft: boolean;
  index: number;
  "data-index"?: number;
};
function StageItem({
  stage,
  isLeft,
  index,
  ...rest
}: StageProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = stage.Icon;
  return <div ref={ref} data-index={rest["data-index"]} className="relative md:grid md:grid-cols-2 md:gap-12 lg:gap-20 items-center">
      {}
      <div aria-hidden="true" className="hidden md:block absolute left-1/2 top-12 -translate-x-1/2 z-10">
        <div className="relative">
          <div className="w-4 h-4 bg-primary rounded-md" />
          <div className="absolute inset-0 w-4 h-4 bg-primary animate-ping rounded-md" style={{
          animationDuration: "2.5s",
          animationDelay: `${index * 0.3}s`
        }} />
        </div>
      </div>

      {}
      <div aria-hidden="true" className="md:hidden absolute left-6 top-6 -translate-x-1/2 z-10">
        <div className="relative">
          <div className="w-3 h-3 bg-primary rounded-md" />
          <div className="absolute inset-0 w-3 h-3 bg-primary animate-ping rounded-md" style={{
          animationDuration: "2.5s",
          animationDelay: `${index * 0.3}s`
        }} />
        </div>
      </div>

      {}
      <div className={`pl-12 md:pl-0 ${isLeft ? "md:col-start-1 md:pr-8 lg:pr-16" : "md:col-start-2 md:pl-8 lg:pl-16"} transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-x-0" : `opacity-0 ${isLeft ? "-translate-x-12" : "translate-x-12"}`}`}>
        <div className="relative p-6 md:p-8 lg:p-10 border-l-4 border-primary bg-card shadow-2xl backdrop-blur-md rounded-md" style={{
        background: "linear-gradient(135deg, hsla(215, 20%, 15%, 0.9) 0%, hsla(215, 25%, 10%, 0.95) 100%)"
      }}>
          {}
          <div className="font-default font-black leading-none mb-4 select-none" style={{
          fontSize: "clamp(4rem, 10vw, 7rem)",
          WebkitTextStroke: "1px hsla(45, 100%, 50%, 0.2)",
          color: "transparent"
        }} aria-hidden="true">
            {stage.number}
          </div>

          <div className="flex items-center gap-3 mb-4">
            <Icon className="w-6 h-6 text-primary" />
            <h3 className="font-default font-bold uppercase text-2xl md:text-3xl tracking-tight text-foreground">
              {stage.title}
            </h3>
          </div>

          <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mb-6">
            {stage.description}
          </p>

          {}
          <div className="relative w-32 h-32 md:w-36 md:h-36 overflow-hidden border border-border rounded-md">
            <img src={stage.image} data-wvc-srcset={stage.srcset} data-wvc-sizes={stage.sizes} alt={stage.alt} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500" />
            <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary" />
            <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-primary" />
          </div>
        </div>
      </div>

      {}
      <div aria-hidden="true" className={`hidden md:block ${isLeft ? "md:col-start-2" : "md:col-start-1 md:row-start-1"}`} />
    </div>;
}